import React, { useState } from 'react';
import { DominionLogo } from './DominionLogo';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { AlertCircle, Loader2 } from 'lucide-react';
import { adminLogin } from '../services/api';

interface AdminLoginProps {
  onLogin: (token: string, email: string) => void;
}

export function AdminLogin({ onLogin }: AdminLoginProps) {
  const { translations: t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await adminLogin({ email, password });
      
      if (response.success && response.access_token) {
        // Store token in localStorage for persistence
        localStorage.setItem('admin_token', response.access_token);
        localStorage.setItem('admin_email', response.user.email);
        
        onLogin(response.access_token, response.user.email);
      } else {
        setError(t.admin.login.invalidCredentials);
      }
    } catch (err: any) {
      const message = err?.message || '';
      console.error('Login error:', message, err);

      // Show the real Supabase error if it's informative, otherwise fallback to translation
      const isGeneric = !message || message === 'Login failed' || message === 'Invalid credentials';
      setError(isGeneric ? t.admin.login.invalidCredentials : message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d12] relative overflow-hidden flex items-center justify-center">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-[#90d977]/20 to-transparent blur-3xl" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-[#90d977]/10 to-transparent blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-md px-4">
        {/* Logo */}
        <div className="flex justify-center mb-12">
          <DominionLogo className="h-8 w-auto" />
        </div>

        {/* Login Card */}
        <div className="bg-[#1a1b25] rounded-lg p-8 shadow-xl">
          <div className="text-center mb-8">
            <h1 className="text-2xl text-white mb-2">
              {t.admin.login.title}
            </h1>
            <p className="text-[#a1a1aa] text-sm">
              {t.admin.login.subtitle}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="email" className="sr-only">
                {t.admin.login.emailPlaceholder}
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.admin.login.emailPlaceholder}
                className="bg-[#0d0d12] border-[#27272a] text-white placeholder:text-[#71717a] focus:border-[#90d977] focus:ring-[#90d977]"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="sr-only">
                {t.admin.login.passwordPlaceholder}
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.admin.login.passwordPlaceholder}
                className="bg-[#0d0d12] border-[#27272a] text-white placeholder:text-[#71717a] focus:border-[#90d977] focus:ring-[#90d977]"
                required
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full bg-[#90d977] hover:bg-[#7bc55f] text-[#0d0d12] cursor-pointer"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t.admin.login.loginButton}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}